import { DndContext, closestCorners, useDroppable, type DragEndEvent } from "@dnd-kit/core";
import { SortableContext, useSortable, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import type { ReactNode } from "react";
import { cn } from "~/lib/utils";
import { useBoardSensors, withoutDragRole } from "./boardDnd";
import { PlanningBoardColumnHeader } from "./PlanningBoardColumnHeader";

/** A board column: stable id (matches columnOf) and a header label. */
export interface DataBoardColumn {
  readonly id: string;
  readonly label: string;
  readonly dotClassName?: string;
  /** Optional status / priority glyph for the column header. */
  readonly leading?: ReactNode;
}

/** Where a dragged card landed. afterCardId present ⇒ dropped onto that card. */
export interface DataBoardMove {
  readonly toColumnId: string;
  readonly afterCardId?: string | undefined;
}

export interface DataBoardProps<Card> {
  readonly columns: ReadonlyArray<DataBoardColumn>;
  readonly cards: ReadonlyArray<Card>;
  /** Stable id for a card (used as the drag id and React key). */
  readonly cardId: (card: Card) => string;
  /** Which column a card belongs to — must return one of columns[].id. */
  readonly columnOf: (card: Card) => string;
  /** Renders the card's visual; DataBoard owns the drag wrapper + click. */
  readonly renderCard: (card: Card) => ReactNode;
  /** Applies a drag result. The surface owns the optimistic reorder + persist. */
  readonly onMove: (cardId: string, move: DataBoardMove) => void;
  /** Opens the card (a plain click, distinct from an 8px drag). */
  readonly onCardClick?: ((card: Card) => void) | undefined;
  /** Quick-add in a column (Linear `+` on the column header). */
  readonly onCreateInColumn?: ((columnId: string) => void) | undefined;
  readonly canCreateInColumn?: ((column: DataBoardColumn) => boolean) | undefined;
  readonly createInColumnLabel?: ((column: DataBoardColumn) => string) | undefined;
  readonly emptyColumnLabel?: string;
  readonly gridClassName?: string;
  /** Suppress position-only reordering while preserving cross-column moves for a filtered subset. */
  readonly reorderingDisabled?: boolean;
  /** Disable every drag mutation while preserving ordinary card clicks. */
  readonly mutationDisabled?: boolean;
}

interface BoardDropOptions {
  readonly preservePosition?: boolean;
}

const COLUMN_DROP_PREFIX = "datacol:";

export function columnDropId(columnId: string): string {
  return `${COLUMN_DROP_PREFIX}${columnId}`;
}

function columnIdFromDropId(value: string): string | null {
  return value.startsWith(COLUMN_DROP_PREFIX) ? value.slice(COLUMN_DROP_PREFIX.length) : null;
}

/**
 * Resolve a drag end into a move, or null for a no-op. Dropping onto another
 * card lands in that card's column, placed after it; dropping onto a column
 * body appends to that column. Pure so the drop rules are unit-testable without
 * simulating pointer events. `columnByCardId` maps every card id to its column.
 */
export function resolveBoardDrop(
  activeId: string,
  overId: string | null,
  columnByCardId: ReadonlyMap<string, string>,
  options: BoardDropOptions = {},
): DataBoardMove | null {
  if (overId === null || !columnByCardId.has(activeId)) return null;

  const sourceColumn = columnByCardId.get(activeId);
  const preservePosition = options.preservePosition ?? true;
  const overColumn = columnByCardId.get(overId);
  if (overColumn !== undefined) {
    if (overId === activeId) return null;
    if (!preservePosition) {
      return overColumn === sourceColumn ? null : { toColumnId: overColumn };
    }
    return { toColumnId: overColumn, afterCardId: overId };
  }

  const columnId = columnIdFromDropId(overId);
  if (columnId === null || (!preservePosition && columnId === sourceColumn)) return null;
  return { toColumnId: columnId };
}

/**
 * Reusable single-row Kanban board: status columns of vertically-sortable cards.
 * Drag a card within a column to reorder or across columns to change grouping;
 * both route through onMove, which the surface applies optimistically. Mirrors
 * the DataTable API — the surface owns card rendering (renderCard) and the move
 * semantics, while DataBoard owns the DnD chrome (sensors, contexts, drop
 * resolution, keyboard drag) so every board behaves identically. See
 * ProjectTaskBoardView for a full integration.
 */
export function DataBoard<Card>({
  columns,
  cards,
  cardId,
  columnOf,
  renderCard,
  onMove,
  onCardClick,
  onCreateInColumn,
  canCreateInColumn,
  createInColumnLabel,
  emptyColumnLabel = "—",
  gridClassName,
  reorderingDisabled = false,
  mutationDisabled = false,
}: DataBoardProps<Card>) {
  const sensors = useBoardSensors();

  const cardsByColumn = new Map<string, Card[]>(columns.map((column) => [column.id, []]));
  const columnByCardId = new Map<string, string>();
  for (const card of cards) {
    const column = columnOf(card);
    cardsByColumn.get(column)?.push(card);
    columnByCardId.set(cardId(card), column);
  }

  const handleDragEnd = (event: DragEndEvent) => {
    if (mutationDisabled) return;
    const move = resolveBoardDrop(
      String(event.active.id),
      event.over ? String(event.over.id) : null,
      columnByCardId,
      { preservePosition: !reorderingDisabled },
    );
    if (move) onMove(String(event.active.id), move);
  };

  const content = (
    <div data-task-board className="min-h-0 overflow-x-auto overscroll-x-contain">
      <div
        className={cn(
          "flex min-h-[420px] items-start gap-3 bg-[var(--task-board-canvas)] p-3",
          gridClassName,
        )}
        style={{ minWidth: `${Math.max(columns.length, 1) * 272}px` }}
      >
        {columns.map((column) => {
          const columnCards = cardsByColumn.get(column.id) ?? [];
          return (
            <BoardColumn
              key={column.id}
              column={column}
              count={columnCards.length}
              onCreate={
                onCreateInColumn != null && (canCreateInColumn?.(column) ?? true)
                  ? () => onCreateInColumn(column.id)
                  : undefined
              }
              createLabel={createInColumnLabel != null ? createInColumnLabel(column) : undefined}
              mutationDisabled={mutationDisabled}
            >
              <SortableContext
                items={columnCards.map((card) => cardId(card))}
                strategy={verticalListSortingStrategy}
              >
                <div className="flex min-h-0 flex-1 flex-col gap-2 px-2 pb-2">
                  {columnCards.length === 0 ? (
                    <div className="flex items-center justify-center py-5 text-sm text-muted-foreground/30">
                      {emptyColumnLabel}
                    </div>
                  ) : (
                    columnCards.map((card) => (
                      <SortableCard
                        key={cardId(card)}
                        id={cardId(card)}
                        onClick={onCardClick ? () => onCardClick(card) : undefined}
                        mutationDisabled={mutationDisabled}
                      >
                        {renderCard(card)}
                      </SortableCard>
                    ))
                  )}
                </div>
              </SortableContext>
            </BoardColumn>
          );
        })}
      </div>
    </div>
  );
  return (
    <DndContext sensors={sensors} collisionDetection={closestCorners} onDragEnd={handleDragEnd}>
      {content}
    </DndContext>
  );
}

function BoardColumn({
  column,
  count,
  children,
  onCreate,
  createLabel,
  mutationDisabled,
}: {
  readonly column: DataBoardColumn;
  readonly count: number;
  readonly children: ReactNode;
  readonly onCreate?: (() => void) | undefined;
  readonly createLabel?: string | undefined;
  readonly mutationDisabled: boolean;
}) {
  const { setNodeRef, isOver } = useDroppable({
    id: columnDropId(column.id),
    disabled: mutationDisabled,
  });
  const leading =
    column.leading ??
    (column.dotClassName != null ? (
      <span className={cn("size-1.5 shrink-0 rounded-full", column.dotClassName)} />
    ) : null);

  return (
    <div
      ref={setNodeRef}
      data-column-id={column.id}
      className={cn(
        "flex w-[260px] shrink-0 flex-col rounded-xl transition-colors duration-150",
        isOver
          ? "bg-[var(--task-board-drop)] ring-1 ring-ring/40"
          : "bg-[var(--task-board-column)]",
      )}
    >
      <PlanningBoardColumnHeader
        label={column.label}
        count={count}
        leading={leading}
        onCreate={onCreate}
        createLabel={createLabel}
      />
      {children}
    </div>
  );
}

function SortableCard({
  id,
  onClick,
  children,
  mutationDisabled,
}: {
  id: string;
  onClick?: (() => void) | undefined;
  children: ReactNode;
  readonly mutationDisabled: boolean;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id,
    disabled: mutationDisabled,
  });
  const style = { transform: CSS.Translate.toString(transform), transition };
  return (
    <div
      ref={setNodeRef}
      style={style}
      {...withoutDragRole(attributes)}
      {...listeners}
      onClick={onClick}
      className={cn("outline-none", isDragging && "opacity-50")}
    >
      {children}
    </div>
  );
}
